import { useState } from 'react';
import { MARKET_PAIRS, type MarketPair } from '../data/mockMarket';
import { useLiveMarket } from '../web3/useLiveMarket';
import TokenIcon from './TokenIcon';

function formatPrice(v: number | undefined) {
  if (v === undefined) return '-';
  if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (v >= 1) return v.toFixed(2);
  return v.toFixed(v < 0.001 ? 7 : 4);
}

export default function MarketSidebar({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (pair: MarketPair) => void;
}) {
  const [query, setQuery] = useState('');

  const filtered = MARKET_PAIRS.filter((p) =>
    `${p.base}${p.quote}`.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <aside className="w-full lg:w-80 h-64 lg:h-auto flex-shrink-0 border-b lg:border-b-0 lg:border-r border-abyss-700 flex flex-col bg-abyss-900/40">
      <div className="p-3 border-b border-abyss-700">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search markets"
          className="w-full px-3 py-2 rounded-md bg-abyss-850 border border-abyss-700 text-sm text-foam-100 placeholder:text-foam-500 outline-none focus:border-tide-500"
        />
      </div>

      <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 text-[11px] uppercase tracking-wide text-foam-500">
        <span>Pair</span>
        <span className="text-right">Price</span>
        <span className="text-right">24h</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.map((pair) => (
          <MarketRow key={pair.id} pair={pair} isActive={pair.id === selected} onSelect={() => onSelect(pair)} />
        ))}
      </div>
    </aside>
  );
}

function MarketRow({ pair, isActive, onSelect }: { pair: MarketPair; isActive: boolean; onSelect: () => void }) {
  const { price, change24h } = useLiveMarket(pair);
  const isUp = change24h >= 0;

  return (
    <button
      onClick={onSelect}
      className={`w-full grid grid-cols-[1fr_auto_auto] items-center gap-2 px-3 py-2.5 text-left transition-colors ${
        isActive ? 'bg-tide-400/10' : 'hover:bg-abyss-800/60'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex -space-x-1.5">
          <TokenIcon symbol={pair.base} size={20} />
          <TokenIcon symbol={pair.quote} size={20} />
        </div>
        <span className="text-sm font-medium text-foam-100 truncate">
          {pair.base}/{pair.quote}
        </span>
      </div>
      <span className="text-sm tabular-nums text-foam-100">{formatPrice(price)}</span>
      <span className={`text-xs tabular-nums font-medium ${isUp ? 'text-tide-400' : 'text-coral-400'}`}>
        {isUp ? '+' : ''}
        {change24h.toFixed(2)}%
      </span>
    </button>
  );
}
